import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiEdit2, FiSave, FiX } from 'react-icons/fi';
import * as FiIcons from 'react-icons/fi';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../lib/supabase';
import toast from 'react-hot-toast';
import ServiceInlineEditor from '../../components/admin/ServiceInlineEditor';

const AdminServiceEditor = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [inlineOpen, setInlineOpen] = useState(false);
  const [editedPricing, setEditedPricing] = useState(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const deepLinkServiceId = params.get('serviceId');

  useEffect(() => {
    fetchServices();
  }, []);

  // When services load and a deep-linked serviceId exists, auto-select
  useEffect(() => {
    if (!loading && services && services.length > 0 && deepLinkServiceId && !selectedService) {
      const sid = (deepLinkServiceId || '').toLowerCase();
      const normalize = (s) => (s || '').toString().trim().toLowerCase();
      const bySlug = services.find(s => normalize(s.slug) === sid);
      const byCode = services.find(s => normalize(s.code) === sid || normalize(s.key) === sid);
      const tokens = sid.replace(/[-_]+/g, ' ').split(' ').filter(Boolean);
      const byNameHeuristic = services.find(s => tokens.every(t => normalize(s.name).includes(t)));
      const match = bySlug || byCode || byNameHeuristic;
      if (match) {
        handleServiceClick(match);
      }
    }
  }, [loading, services, deepLinkServiceId, selectedService]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('services_dy3xr')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleServiceClick = (service) => {
    setSelectedService(service);
    setEditedPricing(service?.detailed_pricing || { online: {}, home: {} });
    setEditMode(false);
  };

  const handleBackToServices = () => {
    setSelectedService(null);
    setEditMode(false);
    setEditedPricing(null);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (!editMode) {
      setEditedPricing(selectedService?.detailed_pricing || { online: {}, home: {} });
    }
  };

  const addPricingCategory = (serviceType) => {
    const categoryName = prompt('Enter category name (e.g., "OC Male", "General", etc.):');
    if (!categoryName || !categoryName.trim()) return;
    const name = categoryName.trim();
    setEditedPricing(prev => {
      const next = { ...(prev || {}), online: { ...(prev?.online || {}) }, home: { ...(prev?.home || {}) } };
      if (!next[serviceType]) next[serviceType] = {};
      if (!next[serviceType][name]) {
        next[serviceType][name] = { examFee: 0, serviceCharge: 0, bankCharge: 50, total: 50 };
      }
      return next;
    });
  };

  const removePricingCategory = (serviceType, category) => {
    setEditedPricing(prev => {
      const next = { ...(prev || {}), online: { ...(prev?.online || {}) }, home: { ...(prev?.home || {}) } };
      if (next[serviceType] && next[serviceType][category]) {
        const { [category]: _, ...rest } = next[serviceType];
        next[serviceType] = rest;
      }
      return next;
    });
  };

  const handlePriceChange = (serviceType, category, field, value) => {
    const numValue = parseFloat(value) || 0;
    setEditedPricing(prev => {
      const updated = { ...prev };
      if (!updated[serviceType]) updated[serviceType] = {};
      if (!updated[serviceType][category]) {
        updated[serviceType][category] = {
          examFee: 0,
          serviceCharge: 0,
          bankCharge: 0,
          total: 0
        };
      }
      
      updated[serviceType][category][field] = numValue;
      
      // Recalculate total
      const pricing = updated[serviceType][category];
      updated[serviceType][category].total = 
        (pricing.examFee || 0) + 
        (pricing.serviceCharge || 0) + 
        (pricing.bankCharge || 0);
      
      return updated;
    });
  };

  const handleSave = async () => {
    try {
      const { error } = await supabase
        .from('services_dy3xr')
        .update({ 
          detailed_pricing: editedPricing,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedService.id);

      if (error) throw error;

      toast.success('Service pricing updated successfully!');
      setEditMode(false);
      
      // Update local state
      setSelectedService({ ...selectedService, detailed_pricing: editedPricing });
      fetchServices();
    } catch (error) {
      console.error('Error updating service:', error);
      toast.error('Failed to update service');
    }
  };

  const getCategoryConfig = (category) => {
    const configs = {
      'application': {
        title: 'Student Services',
        description: 'Educational and academic application services',
        bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100'
      },
      'job-services': {
        title: 'Job Seeker Services',
        description: 'Professional job application and career services',
        bgColor: 'bg-gradient-to-br from-green-50 to-green-100'
      },
      'other': {
        title: 'Other Services',
        description: 'Additional application and verification services',
        bgColor: 'bg-gradient-to-br from-purple-50 to-purple-100'
      }
    };
    return configs[category] || configs['other'];
  };

  const getServiceColor = (color) => {
    const colorMap = {
      'from-blue-500 to-blue-600': 'bg-blue-500',
      'from-green-500 to-green-600': 'bg-green-500',
      'from-purple-500 to-purple-600': 'bg-purple-500',
      'from-red-500 to-red-600': 'bg-red-500',
      'from-indigo-500 to-indigo-600': 'bg-indigo-500',
      'from-yellow-500 to-yellow-600': 'bg-yellow-500',
      'from-pink-500 to-pink-600': 'bg-pink-500',
      'from-teal-500 to-teal-600': 'bg-teal-500',
      'from-orange-500 to-orange-600': 'bg-orange-500',
      'from-gray-500 to-gray-600': 'bg-gray-500'
    };
    return colorMap[color] || 'bg-blue-500';
  };

  const toSlug = (s) => (s || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-_]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

  const getRouteServiceId = (svc) => {
    const cand = svc?.slug || svc?.code || svc?.key;
    if (cand && cand.toString().trim()) return cand.toString().trim();
    return toSlug(svc?.name || 'service');
  };

  const groupedServices = services.reduce((acc, service) => {
    const category = service.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading services...</p>
        </div>
      </div>
    );
  }

  // Service Details View
  if (selectedService) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <button
            onClick={handleBackToServices}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-6"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
            Back to Services
          </button>

          {/* Service Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`${getServiceColor(selectedService.color)} text-white rounded-xl p-8 mb-6`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <SafeIcon icon={FiIcons[selectedService.icon] || FiIcons.FiFileText} className="w-12 h-12 mr-4" />
                <div>
                  <h1 className="text-3xl font-bold">{selectedService.name}</h1>
                  <p className="text-blue-100 mt-1">{selectedService.description}</p>
                </div>
              </div>
              <div className="flex items-center bg-white bg-opacity-20 px-4 py-2 rounded-lg">
                <SafeIcon icon={FiIcons.FiClock} className="w-5 h-5 mr-2" />
                <span>Last Date: 31-12-2024</span>
              </div>
            </div>
          </motion.div>

          {/* Edit Buttons */}
          <div className="flex justify-end mb-4 gap-2">
            <Button variant="outline" onClick={() => setInlineOpen(true)} className="flex items-center">
              <SafeIcon icon={FiIcons.FiEdit2} className="w-4 h-4 mr-2" />
              Edit Dynamic Details
            </Button>
            {editMode ? (
              <div className="space-x-2">
                <Button onClick={handleSave} className="flex items-center">
                  <SafeIcon icon={FiSave} className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
                <Button onClick={handleEditToggle} variant="outline" className="flex items-center">
                  <SafeIcon icon={FiX} className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            ) : (
              <Button onClick={handleEditToggle} className="flex items-center">
                <SafeIcon icon={FiEdit2} className="w-4 h-4 mr-2" />
                Edit Pricing
              </Button>
            )}
          </div>

          {/* Pricing Cards */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Online Service Pricing */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiIcons.FiMonitor} className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">Online Service Pricing</h2>
                  <p className="text-sm text-gray-600">Document upload required</p>
                </div>
                {editMode && (
                  <Button type="button" variant="outline" size="sm" onClick={() => addPricingCategory('online')}>
                    Add Category
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {editedPricing?.online && Object.keys(editedPricing.online).length === 0 && (
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">No online pricing categories yet{editMode ? ' â€” use Add Category to create one' : ''}.</div>
                )}
                {editedPricing?.online && Object.entries(editedPricing.online).map(([category, pricing]) => (
                  <div key={category} className="border-b border-gray-200 pb-4 last:border-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-900">{category}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-green-600">â‚¹{pricing.total || 0}</span>
                        {editMode && (
                          <button onClick={() => removePricingCategory('online', category)} className="text-red-600 hover:text-red-700 text-sm">Remove</button>
                        )}
                      </div>
                    </div>
                    {editMode ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Exam Fee:</span>
                          <input
                            type="number"
                            value={pricing.examFee ?? 0}
                            onChange={(e) => handlePriceChange('online', category, 'examFee', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Service Charge:</span>
                          <input
                            type="number"
                            value={pricing.serviceCharge ?? 0}
                            onChange={(e) => handlePriceChange('online', category, 'serviceCharge', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Bank Charges and Taxes:</span>
                          <input
                            type="number"
                            value={pricing.bankCharge ?? 0}
                            onChange={(e) => handlePriceChange('online', category, 'bankCharge', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        â‚¹{pricing.examFee || 0} + â‚¹{pricing.serviceCharge || 0} + â‚¹{pricing.bankCharge || 0}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>

            {/* Home Service Pricing */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <SafeIcon icon={FiIcons.FiHome} className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900">Home Service Pricing</h2>
                  <p className="text-sm text-gray-600">Professional operator visit your home</p>
                </div>
                {editMode && (
                  <Button type="button" variant="outline" size="sm" onClick={() => addPricingCategory('home')}>
                    Add Category
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {editedPricing?.home && Object.keys(editedPricing.home).length === 0 && (
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">No home pricing categories yet{editMode ? ' â€” use Add Category to create one' : ''}.</div>
                )}
                {editedPricing?.home && Object.entries(editedPricing.home).map(([category, pricing]) => (
                  <div key={category} className="border-b border-gray-200 pb-4 last:border-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-900">{category}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-blue-600">â‚¹{pricing.total || 0}</span>
                        {editMode && (
                          <button onClick={() => removePricingCategory('home', category)} className="text-red-600 hover:text-red-700 text-sm">Remove</button>
                        )}
                      </div>
                    </div>
                    {editMode ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Exam Fee:</span>
                          <input
                            type="number"
                            value={pricing.examFee ?? 0}
                            onChange={(e) => handlePriceChange('home', category, 'examFee', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Service Charge:</span>
                          <input
                            type="number"
                            value={pricing.serviceCharge ?? 0}
                            onChange={(e) => handlePriceChange('home', category, 'serviceCharge', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Bank Charges and Taxes:</span>
                          <input
                            type="number"
                            value={pricing.bankCharge ?? 0}
                            onChange={(e) => handlePriceChange('home', category, 'bankCharge', e.target.value)}
                            className="w-24 px-2 py-1 border border-gray-300 rounded text-right"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-600">
                        â‚¹{pricing.examFee || 0} + â‚¹{pricing.serviceCharge || 0} + â‚¹{pricing.bankCharge || 0}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </div>
  }
          {/* Pricing Breakdown */}
          <Card className="p-6 mt-6">
            <div className="flex items-start">
              <SafeIcon icon={FiIcons.FiInfo} className="w-6 h-6 text-blue-600 mt-1 mr-3" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Pricing Breakdown</h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>â€¢ <strong>Exam Fee:</strong> Official examination fee charged by the institution</li>
                  <li>â€¢ <strong>Service Charge:</strong> Our professional application assistance fee</li>
                  <li>â€¢ <strong>Bank Charges and Taxes:</strong> Transaction processing fee</li>
                  <li>â€¢ <strong>Home Service:</strong> Includes additional â‚¹100 for operator visit</li>
                </ul>
              </div>
            </div>
          </Card>
          {/* Inline dynamic editor overlay */}
          <ServiceInlineEditor
            serviceIdOverride={getRouteServiceId(selectedService)}
            openOverride={inlineOpen}
            onCloseOverride={() => setInlineOpen(false)}
          />
        </div>
      </div>
    );
  }

  // Service Categories View
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Categories</h1>
          <p className="text-gray-600">Choose from our comprehensive range of application services</p>
        </motion.div>

        {/* Service Categories */}
        <div className="space-y-12">
          {Object.entries(groupedServices).map(([category, categoryServices]) => {
            const config = getCategoryConfig(category);
            return (
              <motion.div
                key={category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">{config.title}</h2>
                  <p className="text-gray-600">{config.description}</p>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {categoryServices.map((service) => (
                    <motion.div
                      key={service.id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Card
                        className="p-4 cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => handleServiceClick(service)}
                      >
                        <div className={`${getServiceColor(service.color)} text-white p-4 rounded-lg mb-3 flex justify-center`}>
                          <SafeIcon icon={FiIcons[service.icon] || FiIcons.FiFileText} className="w-8 h-8" />
                        </div>
                        <h3 className="font-semibold text-gray-900 text-center text-sm mb-1">
                          {service.name}
                        </h3>
                        <p className="text-xs text-gray-600 text-center line-clamp-2">
                          {service.description}
                        </p>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminServiceEditor;

